import { useState } from 'react';
import {
  MonitorUp,
  Eye,
  MousePointer,
  PhoneOff,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { RemoteSession, ConnectionState } from '@/hooks/useRemoteSession';

interface RemoteSessionBannerProps {
  session: RemoteSession | null;
  connectionState: ConnectionState;
  onEndSession: () => void;
  isEndingSession?: boolean;
  adminName?: string;
  className?: string;
}

/**
 * A persistent banner shown at the top of the screen when a remote session is active.
 * This ensures the cashier always knows their screen is being viewed/controlled.
 */
export function RemoteSessionBanner({
  session,
  connectionState,
  onEndSession,
  isEndingSession = false,
  adminName,
  className,
}: RemoteSessionBannerProps) {
  const [isMinimized, setIsMinimized] = useState(false);

  // Don't render if no active session
  if (!session || !['connecting', 'active'].includes(session.status)) {
    return null;
  }

  // Don't render if connection failed
  if (connectionState === 'failed' || connectionState === 'disconnected') {
    return null;
  }

  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting';
  const isFullControl = session.session_type === 'full_control';

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className={cn(
          'fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg transition-all',
          isConnected
            ? 'bg-blue-500 hover:bg-blue-600 text-white'
            : 'bg-amber-500 hover:bg-amber-600 text-white'
        )}
      >
        <MonitorUp className="h-4 w-4" />
        {isConnecting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <div className="h-2 w-2 rounded-full bg-white animate-pulse" />
        )}
        <span className="text-sm font-medium">
          {isConnecting ? 'Connecting...' : 'Screen Shared'}
        </span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-50 border-b shadow-md transition-colors',
        isConnected
          ? 'bg-blue-500 border-blue-600'
          : 'bg-amber-500 border-amber-600',
        className
      )}
    >
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Status */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {isConnecting ? (
                <Loader2 className="h-5 w-5 text-white animate-spin" />
              ) : (
                <>
                  <MonitorUp className="h-5 w-5 text-white" />
                  <div className="h-2 w-2 rounded-full bg-white animate-pulse" />
                </>
              )}
            </div>
            <div className="text-white">
              <span className="font-medium">
                {isConnecting ? 'Connecting to remote session...' : 'Screen Sharing Active'}
              </span>
              {adminName && isConnected && (
                <span className="ml-2 text-white/80">
                  - {adminName} is viewing
                </span>
              )}
            </div>
          </div>

          {/* Center: Mode indicator */}
          <div className="hidden sm:flex items-center gap-4 text-white/90 text-sm">
            <div className="flex items-center gap-1.5">
              <Eye className="h-4 w-4" />
              <span>View</span>
            </div>
            {isFullControl && (
              <>
                <span className="text-white/50">+</span>
                <div className="flex items-center gap-1.5">
                  <MousePointer className="h-4 w-4" />
                  <span>Control</span>
                </div>
              </>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-white hover:bg-white/20 hover:text-white"
              onClick={() => setIsMinimized(true)}
            >
              <X className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="h-8 gap-1.5 bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={onEndSession}
              disabled={isEndingSession}
            >
              {isEndingSession ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PhoneOff className="h-4 w-4" />
              )}
              End Session
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A compact indicator that can be placed in the app header.
 */
export function RemoteSessionIndicator({
  session,
  connectionState,
  onClick,
}: {
  session: RemoteSession | null;
  connectionState: ConnectionState;
  onClick?: () => void;
}) {
  // Don't render if no active session
  if (!session || !['connecting', 'active'].includes(session.status)) {
    return null;
  }

  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting';

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
        isConnected
          ? 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 dark:text-blue-400'
          : 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400'
      )}
    >
      <MonitorUp className="h-4 w-4" />
      {isConnecting ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <div className="h-2 w-2 rounded-full bg-current animate-pulse" />
      )}
      <span className="hidden sm:inline">
        {isConnecting ? 'Connecting...' : 'Screen Shared'}
      </span>
    </button>
  );
}

